import { createColumnHelper } from '@tanstack/react-table';
import { PreferenceCardChangeRequest, searchPreferenceCardChangeRequests } from 'client';
import { zSearchPreferenceCardChangeRequestsData } from 'client/zod.gen';
import React from 'react';
import { Await, LoaderFunctionArgs, useLoaderData, useSearchParams } from 'react-router';
import { FilterForm } from 'routes/components/filter-form-context';
import { PreservedNonTableFilterParams } from 'routes/components/preserved-non-table-filter-params';
import invariant from 'tiny-invariant';

import { UrlPagination } from 'DesignSystem/features/pagination/url-pagination';
import { AsyncTableProvider } from 'DesignSystem/features/table-context/async-table-provider';
import { getFilterParamKeys } from 'DesignSystem/features/table-context/filter-param';
import { Table } from 'DesignSystem/features/table-context/table';
import { DefaultTableCell } from 'DesignSystem/features/table-context/table-body';
import { DefaultTableHead } from 'DesignSystem/features/table-context/table-header';
import { useFilterPillItems } from 'DesignSystem/features/table-context/use-table-context';
import LongText from 'components/LongText';
import { formatDate } from 'utils/helpers';
import { looseParsePayload } from 'utils/loose-parse-payload';

import { SortableHeader } from '../components/sortable-header';
import {
  TableLayoutBody,
  TableLayoutFooter,
  TableLayoutHeader,
  TableLayoutHeaderRow,
  TableLayoutRoot,
} from '../components/table-layout';
import TableLink from '../shared/components/table-link';
import PREF_CARDS_BASE_PATH from '../shared/constants';
import { RequestStatusPill } from './components/request-status-badge';
import { StatusFilter, StatusFilterPill } from './components/status-filter';

export async function loader(args: LoaderFunctionArgs) {
  invariant(args.params.cardId, 'cardId is required');
  const searchParams = new URL(args.request.url).searchParams;

  const payload = looseParsePayload(zSearchPreferenceCardChangeRequestsData, {
    body: {
      preference_card_id: args.params.cardId,
      statuses: searchParams.getAll('status'),
      assignee_ids: searchParams.getAll('assignee_id'),
      location_ids: searchParams.getAll('location_id'),
      service_line_ids: searchParams.getAll('service_line_id'),
    },
    query: {
      page: searchParams.get('page') ?? undefined,
      page_size: searchParams.get('page_size') ?? undefined,
      sort: searchParams.get('sort') ?? 'created_date|DESC',
    },
  });

  const results = searchPreferenceCardChangeRequests(payload);

  return {
    items: results.then((res) => res.data?.items ?? []),
    error: results.then((res) => res.error),
    pagination: results.then((res) => ({
      page: res.data?.page,
      page_size: res.data?.page_size,
      total: res.data?.total,
      total_pages: res.data?.total_pages,
    })),
  };
}

export function Component() {
  const loaderData = useLoaderData<typeof loader>();

  return (
    <TableLayoutRoot className="px-6">
      <AsyncTableProvider data={loaderData.items} columns={columns}>
        <TableLayoutHeader>
          <TableLayoutHeaderRow className="gap-6">
            <div className="grow">
              {/* TODO assignee pill FE-387 */}
              <FilterPills />
            </div>
          </TableLayoutHeaderRow>
        </TableLayoutHeader>
        <TableLayoutBody>
          <Table />
        </TableLayoutBody>
        <TableLayoutFooter>
          <Pagination />
        </TableLayoutFooter>
      </AsyncTableProvider>
    </TableLayoutRoot>
  );
}

function Pagination() {
  const loaderData = useLoaderData<typeof loader>();

  return (
    <React.Suspense>
      <Await resolve={loaderData.pagination}>
        {(data) => {
          const page = data.page ?? 1;
          const pageSize = data.page_size ?? 20;
          const total = data.total ?? 0;
          const totalPages = data.total_pages ?? 0;

          return (
            <UrlPagination
              page={page}
              pageSize={pageSize}
              total={total}
              totalPages={totalPages}
              resetPageOnPageSizeChange={false}
            />
          );
        }}
      </Await>
    </React.Suspense>
  );
}

function FilterPills() {
  const formRef = React.useRef<HTMLFormElement>(null);
  const [searchParams] = useSearchParams();
  const items = useFilterPillItems<PreferenceCardChangeRequest>();

  const visibleItems = items.filter((item) => {
    if (item.meta.filter?.pinned) return true;
    const params = getFilterParamKeys(item.meta.filter?.param);
    return params.some((param) => searchParams.has(param));
  });

  if (visibleItems.length === 0) return null;

  return (
    <FilterForm ref={formRef} method="GET" className="flex gap-2">
      {visibleItems.map((item, index) => {
        const { Pill } = item.components;
        if (!Pill) return null;
        const paramKeys = getFilterParamKeys(item.meta.filter?.param);
        const paramKey = paramKeys.join('|');
        const paramValueKey = paramKeys.map((param) => searchParams.getAll(param).join(',')).join('|');
        const baseKey = paramKey || item.meta.label || String(index);
        const key = paramValueKey ? `${baseKey}:${paramValueKey}` : baseKey;
        return <Pill key={key} formRef={formRef} meta={item.meta} />;
      })}
      <PreservedNonTableFilterParams />
    </FilterForm>
  );
}

const columnHelper = createColumnHelper<PreferenceCardChangeRequest>();
const columns = [
  columnHelper.accessor('client_card_id', {
    id: 'client_card_id',
    size: 110,
    meta: {
      label: 'Request ID',
      sort: {
        param: 'sequence',
      },
    },
    header: (props) => (
      <DefaultTableHead header={props.header}>
        <SortableHeader column={props.column} />
      </DefaultTableHead>
    ),
    cell: (props) => (
      <DefaultTableCell cell={props.cell}>
        <TableLink
          to={`${PREF_CARDS_BASE_PATH}/${props.row.original.preference_card_id}/requests/${props.row.original.id}`}
        >
          <LongText text={`${props.row.original.client_card_id}-${props.row.original.sequence}`} />
        </TableLink>
      </DefaultTableCell>
    ),
  }),
  columnHelper.accessor('status', {
    id: 'status',
    size: 149,
    meta: {
      label: 'Status',
      sort: {
        param: 'status',
      },
      filter: {
        param: 'status',
        pinned: true,
        components: {
          Filter: StatusFilter,
          Pill: StatusFilterPill,
        },
      },
    },
    header: (props) => (
      <DefaultTableHead header={props.header}>
        <SortableHeader column={props.column} />
      </DefaultTableHead>
    ),
    cell: (props) => (
      <DefaultTableCell cell={props.cell}>
        <RequestStatusPill status={props.row.original.status} />
      </DefaultTableCell>
    ),
  }),
  columnHelper.accessor('procedure_name', {
    id: 'procedure_name',
    meta: {
      label: 'Description',
      sort: {
        param: 'procedure_name',
      },
    },
    header: (props) => (
      <DefaultTableHead header={props.header}>
        <SortableHeader column={props.column} />
      </DefaultTableHead>
    ),
    cell: (props) => (
      <DefaultTableCell cell={props.cell} className="max-w-[280px]">
        <LongText text={props.row.original.procedure_name ?? '–'} />
      </DefaultTableCell>
    ),
  }),
  columnHelper.accessor('assignee.name', {
    id: 'assignee_name',
    size: 149,
    meta: {
      label: 'Assignee',
      sort: {
        param: 'assignee_name',
      },
    },
    header: (props) => (
      <DefaultTableHead header={props.header}>
        <SortableHeader column={props.column} />
      </DefaultTableHead>
    ),
    cell: (props) => <DefaultTableCell cell={props.cell}>{props.row.original.assignee?.name ?? '–'}</DefaultTableCell>,
  }),
  columnHelper.accessor('requestor.name', {
    id: 'requestor_name',
    size: 149,
    meta: {
      label: 'Requestor',
    },
    header: (props) => (
      <DefaultTableHead header={props.header}>
        <SortableHeader column={props.column} />
      </DefaultTableHead>
    ),
    cell: (props) => <DefaultTableCell cell={props.cell}>{props.row.original.requestor?.name ?? '–'}</DefaultTableCell>,
  }),
  columnHelper.accessor('created_date', {
    id: 'created_date',
    size: 100,
    meta: {
      label: 'Created',
      sort: {
        param: 'created_date',
      },
    },
    header: (props) => (
      <DefaultTableHead header={props.header}>
        <SortableHeader column={props.column} />
      </DefaultTableHead>
    ),
    cell: (props) => (
      <DefaultTableCell cell={props.cell}>
        {props.row.original.created_date
          ? formatDate(props.row.original.created_date?.toString(), '2-digit', '2-digit', '2-digit')
          : '–'}
      </DefaultTableCell>
    ),
  }),
];
